<script setup lang="ts">
import type { BillingInterval } from '@/utils/billing';
import { yearlySavingsPercent } from '@/utils/billing';
import SegmentedControl from '@/core/elements/SegmentedControl.vue';
import type { ISelectOption } from '@/interfaces';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue: BillingInterval;
    savingsPercent?: number;
  }>(),
  {
    savingsPercent: yearlySavingsPercent,
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: BillingInterval];
}>();

const { t } = useI18n();

const intervalOptions = computed<ISelectOption<BillingInterval>[]>(() => [
  { value: 'year', label: t('billing.intervalToggle.yearly') },
  { value: 'month', label: t('billing.intervalToggle.monthly') },
]);
</script>

<template>
  <div class="flex min-w-0 items-center w-full max-md:flex-col max-md:gap-sm">
    <div class="hidden max-w-[280px] text-center leading-[150%] max-md:block">
      <span class="text-green font-semibold">
        🎁 {{ $t('billing.intervalToggle.yearlySavingsLabel', { percent: props.savingsPercent }) }}
      </span>
      {{ $t('billing.intervalToggle.withYearlyBilling') }}
    </div>
    <div class="bg-surface-divider h-px flex-1 max-md:hidden" />
    <SegmentedControl
      :model-value="modelValue"
      :options="intervalOptions"
      width="mobile-full"
      :aria-label="$t('billing.intervalToggle.ariaLabel')"
      @update:model-value="(v: BillingInterval) => emit('update:modelValue', v)"
    >
      <template #option-year>
        <span class="inline-flex max-w-full min-w-0 items-center gap-xs max-md:hidden">
          <span class="text-green shrink-0">
            🎁 {{ $t('billing.intervalToggle.yearlySavingsLabel', { percent: props.savingsPercent }) }}
          </span>
          <span>{{ $t('billing.intervalToggle.withYearlyBilling') }}</span>
        </span>
        <span class="md:hidden">{{ $t('billing.intervalToggle.yearlyShort') }}</span>
      </template>
      <template #option-month="{ option }">
        <span class="max-md:hidden">{{ option.label }}</span>
        <span class="md:hidden">{{ $t('billing.intervalToggle.monthlyShort') }}</span>
      </template>
    </SegmentedControl>
    <div class="bg-surface-divider h-px flex-1 max-md:hidden" />
  </div>
</template>
